import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { CurrencyBadge } from '@/components/CurrencyBadge'
import { PageHeader } from '@/app/layouts/PageHeader'
import { NotFoundScreen } from '@/features/system/NotFoundScreen'
import { Skeleton } from '@/components/States'
import { useRepository } from '@/data/repository-context'
import { useQuery } from '@/data/use-query'
import { formatMoney, fromMajor } from '@/lib/money'
import { cn } from '@/lib/cn'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import send from '@/features/transfers/SendAbroadScreen.module.css'
import styles from './Activate.module.css'
import { CardFields } from './PaymentMethods'

/** The opening fee was dropped for a first deposit that stays the customer's. */
const MINIMUM = 50
const PRESETS = [50, 100, 250, 500]

const SOURCES = [
  {
    id: 'balance',
    label: 'US Dollar account',
    detail: '$18,240.50 available',
    rate: 0,
    icon: 'account_balance_wallet',
  },
  { id: 'debit', label: 'Debit card', detail: 'Instant', rate: 0.005, icon: 'credit_card' },
  { id: 'bank', label: 'Bank transfer', detail: 'Same day', rate: 0, icon: 'account_balance' },
]

export function ActivateAccountScreen() {
  const navigate = useNavigate()
  const { id } = useParams()
  const repository = useRepository()
  const accounts = useQuery(() => repository.listAccounts(), [repository])

  const [amount, setAmount] = useState(String(MINIMUM))
  const [source, setSource] = useState('balance')
  const [cardOk, setCardOk] = useState(false)

  const account = accounts.data?.find((item) => item.id === id)
  const picked = SOURCES.find((item) => item.id === source) ?? SOURCES[0]!

  const model = useMemo(() => {
    const parsed = Number.parseFloat(amount.replace(/,/g, ''))
    const value = Number.isFinite(parsed) && parsed > 0 ? parsed : 0
    return {
      deposit: fromMajor(value, 'USD'),
      fee: fromMajor(value * picked.rate, 'USD'),
      total: fromMajor(value * (1 + picked.rate), 'USD'),
      short: value < MINIMUM,
    }
  }, [amount, picked])

  if (accounts.status === 'loading') return <Skeleton height={360} />
  if (!account) return <NotFoundScreen />

  return (
    <>
      <PageHeader
        title={`Activate your ${account.label} account`}
        subtitle="Opening it was free. It goes live with your first deposit, and that money stays yours."
        actions={
          <Button variant="ghost" size="md" iconStart="arrow_back" to="/accounts">
            Back to accounts
          </Button>
        }
      />

      <div className={checkout.grid}>
        <Stack gap={3}>
          <Card tone="light">
            <Stack direction="row" align="center" gap={3}>
              <CurrencyBadge currency={account.currency} size={40} />
              <Stack grow gap={0}>
                <Text variant="caption" weight="semibold">
                  {account.label} · {account.maskedNumber}
                </Text>
                <Text variant="caption" tone="secondary">
                  {account.rail} · details already issued in your name
                </Text>
              </Stack>
              <Pill tone="warning">Awaiting deposit</Pill>
            </Stack>
          </Card>

          <Card tone="light">
            <Text variant="caption" weight="semibold">
              How much to start with?
            </Text>
            <div className={`${send.amountField} pb-mt-4`}>
              <Text variant="caption" tone="secondary">
                Minimum {formatMoney(fromMajor(MINIMUM, 'USD'))} · it lands in the account, not with
                us
              </Text>
              <div className={send.amountRow}>
                <CurrencyBadge currency="USD" size={36} />
                <input
                  className={send.amountInput}
                  inputMode="decimal"
                  value={amount}
                  aria-label="First deposit"
                  onChange={(event) => setAmount(event.target.value.replace(/[^\d.,]/g, ''))}
                />
                <Text variant="subtitle" weight="semibold" tone="secondary">
                  USD
                </Text>
              </div>
            </div>
            <div className={`${styles.presets} pb-mt-4`}>
              {PRESETS.map((preset) => (
                <Button
                  key={preset}
                  variant={String(preset) === amount ? 'secondary' : 'outline'}
                  size="sm"
                  onClick={() => setAmount(String(preset))}
                >
                  ${preset}
                </Button>
              ))}
            </div>
            {model.short && (
              <Stack direction="row" align="center" gap={2} className="pb-mt-4">
                <Icon name="error" size={16} />
                <Text variant="caption" tone="negative" weight="semibold">
                  The first deposit has to be at least {formatMoney(fromMajor(MINIMUM, 'USD'))}.
                </Text>
              </Stack>
            )}
          </Card>

          <Card tone="light">
            <Text variant="caption" weight="semibold">
              Where from?
            </Text>
            <Stack gap={2} className="pb-mt-3">
              {SOURCES.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={cn(send.corridor, item.id === source && send.corridorActive)}
                  onClick={() => setSource(item.id)}
                  aria-pressed={item.id === source}
                >
                  <Icon name={item.icon} size={20} />
                  <Stack grow gap={0} align="start">
                    <Text variant="caption" weight="semibold">
                      {item.label}
                    </Text>
                    <Text variant="caption" tone="secondary">
                      {item.detail}
                    </Text>
                  </Stack>
                  <Text variant="caption" weight="semibold" numeric>
                    {item.rate === 0 ? 'Free' : `${(item.rate * 100).toFixed(1)}%`}
                  </Text>
                </button>
              ))}
            </Stack>
            {source === 'debit' && (
              <div className="pb-mt-4">
                <CardFields onValid={setCardOk} />
              </div>
            )}
            {source === 'bank' && (
              <Stack gap={1} className="pb-mt-4">
                <Text variant="caption" weight="semibold">
                  Send {formatMoney(model.deposit)} from your bank to
                </Text>
                <Text variant="caption" tone="secondary" numeric>
                  {account.label} · {account.maskedNumber} · {account.rail}
                </Text>
                <Text variant="caption" tone="secondary">
                  The account goes live the moment it lands, usually the same day.
                </Text>
              </Stack>
            )}
          </Card>
        </Stack>

        <Stack gap={3} className={checkout.summary}>
          <Card tone="dark">
            <Card.Header>
              <Text variant="eyebrow" weight="semibold" tone="onDarkMuted">
                Lands in your account
              </Text>
              <Pill tone="accent">Stays yours</Pill>
            </Card.Header>
            <Text as="p" variant="display" weight="bold" tone="accent" numeric className="pb-mt-3">
              {formatMoney(model.deposit)}
            </Text>

            <div className={checkout.rule} />

            {[
              ['Account opening', 'Free'],
              ['First deposit', formatMoney(model.deposit)],
              [
                picked.rate === 0
                  ? 'Transfer fee'
                  : `Card fee · ${(picked.rate * 100).toFixed(1)}%`,
                picked.rate === 0 ? 'None' : formatMoney(model.fee),
              ],
              ['Monthly fee', 'None'],
            ].map(([label, value], index) => (
              <div key={label} className={checkout.line}>
                <Text variant="caption" tone="onDarkMuted">
                  {label}
                </Text>
                <Text
                  variant="caption"
                  weight="semibold"
                  tone={index === 0 || index === 3 ? 'accent' : 'onDark'}
                  numeric
                >
                  {value}
                </Text>
              </div>
            ))}

            <div className={checkout.rule} />

            <div className={checkout.line}>
              <Text variant="caption" weight="semibold" tone="onDark">
                Total to pay
              </Text>
              <Text variant="subtitle" weight="bold" tone="onDark" numeric>
                {formatMoney(model.total)}
              </Text>
            </div>

            <Card.Footer>
              <Button
                size="lg"
                block
                iconStart="lock"
                disabled={model.short || (source === 'debit' && !cardOk)}
                onClick={() =>
                  navigate(
                    source === 'bank'
                      ? `/activate/account/done?pending=1&deposit=${model.deposit.minorUnits}`
                      : `/activate/account/done?deposit=${model.deposit.minorUnits}`,
                    { replace: true },
                  )
                }
              >
                {source === 'bank'
                  ? 'I have sent it'
                  : picked.rate
                    ? `Pay ${formatMoney(model.total)} and activate`
                    : `Deposit ${formatMoney(model.deposit)} and activate`}
              </Button>
              <Stack direction="row" align="center" gap={2} className="pb-mt-3">
                <Icon name="savings" size={15} />
                <Text variant="caption" tone="onDarkMuted">
                  Nothing is deducted from the deposit. Withdraw it whenever you like.
                </Text>
              </Stack>
            </Card.Footer>
          </Card>
        </Stack>
      </div>
    </>
  )
}
